import _ from 'lodash'
import { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

/** API */
import RegisterApi from 'api/counter/register.api'
import ClinicApi from 'api/master/clinic.api'
import PromotionsApi from 'api/promotion/promotion.api'
import PaymentChannelsApi from 'api/setting/finances/payment-channels.api'
import VoucherApi from 'api/voucher/voucher.api'

/** UTILS || SLICE */
import {
    resetPaymentChannelByKey,
    setPaymentByKey
} from 'app/slice/payment.slice'
import { TreatmentStateInterface } from 'app/slice/treatment.slice'
import { getBranch, getCrm } from 'utils/app.utils'

/** TYPES */
import { ModalCustomProps, PatientFinanceInterface } from '../types/types'

interface UsePaymentDataLoaderProps extends ModalCustomProps {
    treatments: TreatmentStateInterface
}

export const usePaymentDataLoader = (props: UsePaymentDataLoaderProps) => {
    const dispatch = useDispatch()
    const { treatments } = props

    const [patientFinance, setPatientFinance] = useState<PatientFinanceInterface>({
        advanceBalance: 0,
        outstandingBalance: 0,
        treatmentRights: []
    })
    const [banks, setBanks] = useState<any[]>([])
    const [billPrefixes, setBillPrefixes] = useState([])
    const [defaultPaymentChannels, setDefaultPaymentChannels] = useState([])
    const [promotionList, setPromotionList] = useState<any>([])
    const [voucherList, setVoucherPayment] = useState<any>({})

    // ✅ OPTIMIZED: Memoized static function calls
    const branchIdMemo = useMemo(() => getBranch(), [])

    // Load Patient Finance Data
    const loadPatientFinance = useCallback(async () => {
        const resPatientFinance: any = await RegisterApi.findPatientFinanceByPatientId(
            props.patient?.patientId || props.patient?.order?.patientId
        )

        if (resPatientFinance.status === 200) {
            const treatmentRights = resPatientFinance.data.patientTreatmentRights

            let advanceBalance = parseFloat(resPatientFinance.data.advanceBalance)

            // Adjust for edit payment
            if (props?.detail?.orderPaymentChannels) {
                for (const paymentChannel of props.detail.orderPaymentChannels) {
                    if (paymentChannel.paymentChannelId === 5) {
                        const treatmentRight = _.find(treatmentRights, {
                            treatmentRightId: parseInt(paymentChannel.treatmentRightId)
                        })
                        const treatmentRightIndex = _.findIndex(treatmentRights, {
                            treatmentRightId: parseInt(paymentChannel.treatmentRightId)
                        })
                        if (treatmentRight) {
                            if (treatmentRight.isDivide === '0') {
                                treatmentRight.balance = treatmentRight.balance + 1
                                treatmentRight.amountUsed = treatmentRight.amountUsed - 1
                            } else {
                                treatmentRight.balance = treatmentRight.balance + paymentChannel.amountPaid
                                treatmentRight.amountUsed = treatmentRight.amountUsed - paymentChannel.amountPaid
                            }
                            treatmentRight.amountPaid = paymentChannel.amountPaid
                            treatmentRights[treatmentRightIndex] = treatmentRight
                        }
                    }
                    if (paymentChannel.paymentChannelId === 6) {
                        advanceBalance += parseFloat(paymentChannel.amountPaid)
                    }
                }
            }

            setPatientFinance({
                advanceBalance: advanceBalance,
                outstandingBalance: resPatientFinance.data.outstandingBalance,
                treatmentRights: treatmentRights
            })
        }
    }, [props.patient, props.detail])

    // Load Promotions and Vouchers
    const loadPromotionsAndVouchers = useCallback(async () => {
        if (!getCrm()) return

        if (props?.detail) {
            const promotionOp = props?.detail?.orderPaymentChannels?.find(
                (item: any) => item?.paymentChannelId === 8 && item?.amountPaid > 0
            )
            const voucherOp = props?.detail?.orderPaymentChannels?.find(
                (item: any) => item?.paymentChannelId === 7 && item?.amountPaid > 0
            )

            if (promotionOp?.amountPaid) {
                setPromotionList([{
                    promotionName: `${promotionOp?.promotionCode} ${promotionOp?.promotionName}`,
                    promotionBranchId: promotionOp?.promotionBranchId,
                    promotionId: promotionOp?.promotionId,
                    detail: promotionOp?.promotionDetail || ''
                }])
            }

            if (voucherOp?.amountPaid) {
                setVoucherPayment([{
                    voucherCode: voucherOp?.voucherCode || '',
                    voucherName: `${voucherOp?.voucherName}`,
                    voucherListId: voucherOp?.voucherListId,
                    voucherId: voucherOp?.voucherId || 999,
                    detail: voucherOp?.voucherDetail || ''
                }])
            }
        } else {
            if (props.systemConfig.menu === "RETROSPECTIVE") {
                const resp = await PromotionsApi.findPromotionTreatments({
                    branchId: branchIdMemo,
                    patientId: props.patient?.patientId,
                    items: _.map(treatments.summeryTreatments, (item) => ({
                        itemType: item.group || 'OPERATIVE',
                        itemId: item.operativeId || item.itemId,
                        qty: item.qty
                    })),
                    totalPrice: _.sumBy(treatments.summeryTreatments, 'total'),
                    date: props.date,
                    isPayment: '1'
                })
                setPromotionList(resp.data?.promotions)
                setVoucherPayment(resp.data?.vouchers)
            } else {
                const patientCaseId = props.patientCaseId || props?.patient?.patientCase?.patientCaseId || props?.detail?.order?.patientCaseId

                const promotionRes = await PromotionsApi.findPromotionPayment(patientCaseId)
                const newData = promotionRes?.data?.filter((item: any) => item?.type !== "EXTRA") || []
                setPromotionList(newData)

                const voucherRes = await VoucherApi.findVoucherPatientAll(patientCaseId)
                setVoucherPayment(voucherRes.data)
            }
        }
    }, [
        props.detail,
        props.systemConfig.menu,
        props.patient,
        props.patientCaseId,
        props.date,
        treatments.summeryTreatments,
        branchIdMemo
    ])

    // Load Banks
    const loadBanks = useCallback(async () => {
        const resBanks = await ClinicApi.findAllBanks(branchIdMemo)
        if (resBanks.status === 200) {
            setBanks(resBanks.data.sort((a: any, b: any) => a.bankId - b.bankId))
        }
    }, [branchIdMemo])

    // Load Bill Prefixes
    const loadBillPrefixes = useCallback(async () => {
        const resBillPrefixes = await ClinicApi.allBillPrefix()
        setBillPrefixes(resBillPrefixes.data)
        dispatch(setPaymentByKey({ key: 'prefix', value: resBillPrefixes?.data?.[0]?.prefix || '' }))
    }, [dispatch])

    // Load Default Payment Channels
    const loadDefaultPaymentChannels = useCallback(async () => {
        const resp = await PaymentChannelsApi.findAll({})
        setDefaultPaymentChannels(resp.data)
    }, [])



    // Main Load Data Function
    const loadData = useCallback(async () => {
        if (!props.isShow) return

        dispatch(resetPaymentChannelByKey())

        // Load all data in parallel — ใช้ allSettled เพื่อไม่ให้ modal ค้างถ้ามี API ตัวเดียว fail/slow
        const results = await Promise.allSettled([
            loadPatientFinance(),
            loadPromotionsAndVouchers(),
            loadBanks(),
            loadBillPrefixes(),
            loadDefaultPaymentChannels()
        ])
        results.forEach((r, i) => {
            if (r.status === 'rejected') {
                console.error(`[usePaymentDataLoader] load #${i} failed:`, r.reason)
            }
        })
    }, [
        props.isShow,
        loadPatientFinance,
        loadPromotionsAndVouchers,
        loadBanks,
        loadBillPrefixes,
        loadDefaultPaymentChannels,
        dispatch
    ])

    return {
        patientFinance,
        banks,
        billPrefixes,
        defaultPaymentChannels,
        promotionList,
        voucherList,
        loadData
    }
}
